// SGNL-269 — Admin "MCP Access" sub-tab.
//
// Admin-only surface (mounted inside the already-gated Admin tab) to mint,
// list, and revoke MCP API keys for the internet-reachable /api/mcp endpoint.
// Every key is bound to an IP allowlist, a forced 30/60/90-day expiry, is
// rate-limited, and audited (see docs/MCP_ACCESS.md). The raw secret is shown
// exactly ONCE at creation (one-time reveal modal).

import { esc } from "../lib/format";
import type { McpKey, McpKeyCreated } from "../types";

function showError(host: HTMLElement, message: string): void {
  let banner = host.querySelector<HTMLDivElement>('[data-testid="mcp-error-banner"]');
  if (!banner) {
    banner = document.createElement("div");
    banner.dataset.testid = "mcp-error-banner";
    banner.setAttribute("role", "alert");
    banner.className = "error";
    banner.style.cssText =
      "margin:8px 0;padding:8px 12px;background:#FFE2E5;color:#9F1230;border:1px solid #FFC8CE;border-radius:6px;font-size:13px;";
    host.insertBefore(banner, host.firstChild);
  }
  banner.textContent = message;
  setTimeout(() => banner?.remove(), 6000);
}

async function listKeys(): Promise<McpKey[]> {
  const r = await fetch("/api/mcp-keys", { credentials: "same-origin" });
  if (!r.ok) throw new Error(`Load keys failed (${r.status}): ${await r.text()}`);
  return ((await r.json()) as { keys: McpKey[] }).keys;
}

async function createKey(body: {
  user_email: string;
  name: string;
  expiry_days: number;
  allowed_ips: string[];
}): Promise<McpKeyCreated> {
  const r = await fetch("/api/mcp-keys", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`Create key failed (${r.status}): ${await r.text()}`);
  return (await r.json()) as McpKeyCreated;
}

async function revokeKey(id: number): Promise<void> {
  const r = await fetch(`/api/mcp-keys/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!r.ok && r.status !== 204) throw new Error(`Revoke failed (${r.status}): ${await r.text()}`);
}

function _date(iso: string | null): string {
  return iso ? esc(iso.slice(0, 10)) : "—";
}

function _status(k: McpKey): string {
  if (k.revoked_at) return '<span class="chip coral">revoked</span>';
  if (k.expires_at && new Date(k.expires_at).getTime() <= Date.now())
    return '<span class="chip">expired</span>';
  return '<span class="chip aqua">active</span>';
}

/** One-time reveal modal for a freshly-minted key. */
function showRevealModal(created: McpKeyCreated): void {
  const backdrop = document.createElement("div");
  backdrop.className = "rc-drill-backdrop";
  backdrop.dataset.testid = "mcp-reveal-modal";
  backdrop.innerHTML = `
    <div class="rc-drill-modal" role="dialog" aria-modal="true" aria-label="New MCP key">
      <div class="rc-drill-modal__header">
        <strong>Key created — copy it now</strong>
        <button type="button" class="rc-drill-modal__close" data-act="close" aria-label="Close">×</button>
      </div>
      <div class="rc-drill-modal__body">
        <p class="muted" style="margin:0 0 8px;">
          This is the <strong>only time</strong> the full key is shown. Store it securely and hand it to
          <strong>${esc(created.user_email)}</strong>. It works only from the allowlisted IP(s)
          <strong>${esc(created.allowed_ips ?? "")}</strong> and expires ${_date(created.expires_at)}.
        </p>
        <div style="display:flex;gap:8px;align-items:center;">
          <input type="text" readonly data-testid="mcp-raw-key" value="${esc(created.raw_key)}"
            style="flex:1;font-family:monospace;font-size:12px;padding:6px 8px;" />
          <button type="button" class="btn-primary" data-act="copy">Copy</button>
        </div>
      </div>
    </div>`;
  const close = () => backdrop.remove();
  backdrop.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t === backdrop || t.dataset.act === "close") close();
    if (t.dataset.act === "copy") {
      void navigator.clipboard?.writeText(created.raw_key);
      t.textContent = "Copied ✓";
    }
  });
  document.body.appendChild(backdrop);
}

function howToPanel(): string {
  return `
    <details class="mcp-howto" style="margin:12px 0;">
      <summary style="cursor:pointer;font-weight:600;">How to use an MCP key</summary>
      <div class="muted" style="margin-top:8px;font-size:13px;line-height:1.5;">
        <p><strong>Endpoint:</strong> <code>POST https://signal.bounteous.app/api/mcp</code>
           &nbsp;·&nbsp; <strong>Auth:</strong> <code>Authorization: Bearer sk_signal_…</code></p>
        <p><strong>Tools (read-only):</strong> <code>search_opportunities</code>,
           <code>get_account_dossier</code>, <code>get_opportunity</code> — JSON-RPC
           <code>tools/list</code> / <code>tools/call</code>.</p>
        <p><strong>Rules:</strong> a key works only from its allowlisted IP(s) (your egress IP —
           roaming laptops won't work, by design), is RBAC-scoped to its owner, rate-limited
           (5 calls / 60s), and every call is audited. See <code>docs/MCP_ACCESS.md</code>.</p>
      </div>
    </details>`;
}

export async function renderMcpKeysPanel(host: HTMLElement, currentUserEmail: string): Promise<void> {
  host.innerHTML = `<div class="loading">Loading MCP keys…</div>`;
  let keys: McpKey[];
  try {
    keys = await listKeys();
  } catch (e) {
    host.innerHTML = `<div class="error">${esc(e instanceof Error ? e.message : String(e))}</div>`;
    return;
  }

  const rows = keys
    .map(
      (k) => `
      <tr data-key-id="${k.id}">
        <td>${esc(k.user_email)}</td>
        <td>${esc(k.name)}</td>
        <td><code>${esc(k.key_prefix)}</code></td>
        <td>${esc(k.allowed_ips ?? "—")}</td>
        <td>${_status(k)}</td>
        <td>${_date(k.created_at)}</td>
        <td>${_date(k.last_used_at)}</td>
        <td>${_date(k.expires_at)}</td>
        <td>${
          k.revoked_at
            ? ""
            : `<button type="button" class="action-link" data-act="revoke" data-id="${k.id}">Revoke</button>`
        }</td>
      </tr>`,
    )
    .join("");

  host.innerHTML = `
    <div data-testid="mcp-access-panel">
      ${howToPanel()}
      <div class="filters" style="flex-wrap:wrap;gap:8px;align-items:end;">
        <input type="text" data-f="user_email" placeholder="target user email"
          value="${esc(currentUserEmail)}" style="min-width:220px;" />
        <input type="text" data-f="name" placeholder="key name (e.g. constellation-agent)" style="min-width:200px;" />
        <select data-f="expiry" data-testid="mcp-expiry">
          <option value="30">Expires 30 days</option>
          <option value="60">Expires 60 days</option>
          <option value="90">Expires 90 days</option>
        </select>
        <input type="text" data-f="allowed_ips" placeholder="allowed IPs/CIDRs, comma-separated"
          style="min-width:260px;" />
        <button type="button" class="btn-primary" data-act="create" data-testid="mcp-create">Create key</button>
      </div>
      <div class="table-wrap" style="margin-top:12px;">
        <table class="data-table" data-testid="mcp-keys-table">
          <thead><tr>
            <th>User</th><th>Name</th><th>Prefix</th><th>Allowed IPs</th><th>Status</th>
            <th>Created</th><th>Last used</th><th>Expires</th><th></th>
          </tr></thead>
          <tbody>${rows || `<tr><td colspan="9" class="muted">No keys yet.</td></tr>`}</tbody>
        </table>
      </div>
    </div>`;

  host.addEventListener("click", async (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.act === "create") {
      const get = (f: string) =>
        (host.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-f="${f}"]`)?.value ?? "").trim();
      const allowed = get("allowed_ips")
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const user_email = get("user_email");
      const name = get("name");
      if (!user_email || !name) return showError(host, "Target user and name are required.");
      if (allowed.length === 0) return showError(host, "At least one allowed IP/CIDR is required.");
      t.setAttribute("disabled", "true");
      try {
        const created = await createKey({
          user_email,
          name,
          expiry_days: Number(get("expiry")),
          allowed_ips: allowed,
        });
        showRevealModal(created);
        await renderMcpKeysPanel(host, currentUserEmail);
      } catch (err) {
        showError(host, err instanceof Error ? err.message : String(err));
        t.removeAttribute("disabled");
      }
    } else if (t.dataset.act === "revoke") {
      const id = Number(t.dataset.id);
      if (!confirm("Revoke this key? It stops working immediately.")) return;
      try {
        await revokeKey(id);
        await renderMcpKeysPanel(host, currentUserEmail);
      } catch (err) {
        showError(host, err instanceof Error ? err.message : String(err));
      }
    }
  });
}
